import { Shield } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

interface SSOLoginFormProps {
  email: string;
  ssoProvider?: string;
  orgName?: string;
  onBackToEmail: () => void;
  onSSOLogin: (provider: string) => void;
  onUseRegularLogin: () => void;
}

export function SSOLoginForm({
  email,
  ssoProvider,
  orgName,
  onBackToEmail,
  onSSOLogin,
  onUseRegularLogin,
}: SSOLoginFormProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleSSOLogin = async (provider: string) => {
    setIsLoading(true);
    try {
      onSSOLogin(provider);
    } finally {
      setIsLoading(false);
    }
  };

  const getSSOProviderName = (provider?: string) => {
    switch (provider?.toLowerCase()) {
      case 'google':
        return 'Google';
      case 'microsoft':
        return 'Microsoft';
      case 'okta':
        return 'Okta';
      case 'saml':
        return 'SAML';
      default:
        return 'SSO';
    }
  };

  const getSSOProviderIcon = (provider?: string) => {
    return <Shield className="w-5 h-5" />;
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full">
          {getSSOProviderIcon(ssoProvider)}
        </div>
        <h3 className="mb-2 text-lg font-semibold text-neutral-950">
          {orgName ? `${orgName} uses SSO` : 'SSO Available'}
        </h3>
        <p className="text-sm text-neutral-600">
          Your organization has Single Sign-On configured for <strong>{email}</strong>
        </p>
      </div>

      <div className="space-y-3">
        <Button
          onClick={() => handleSSOLogin(ssoProvider || 'sso')}
          disabled={isLoading}
          className="flex items-center justify-center w-full gap-3 font-medium bg-black h-11 hover:bg-gray-800"
          size="lg"
        >
          {getSSOProviderIcon(ssoProvider)}
          {isLoading ? (
            <>
              <Spinner size={16} color="white" />
              Redirecting...
            </>
          ) : (
            `Continue with ${getSSOProviderName(ssoProvider)}`
          )}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-neutral-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-neutral-500">or</span>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={onUseRegularLogin}
          disabled={isLoading}
          className="w-full h-11 border-neutral-200 hover:bg-neutral-50"
          size="lg"
        >
          Use email and password instead
        </Button>
      </div>

      <button
        onClick={onBackToEmail}
        disabled={isLoading}
        className="w-full text-sm font-medium text-center transition-colors hover:underline"
      >
        ← Try a different email
      </button>
    </div>
  );
}
